import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toastify';
import JobDetail from './JobDetails';
import JobMatchingJobs from './NewJobMatchingJobs';
import NewJobInvoice from './NewJobInvoice';
import NewJobNote from './NewJobNotes';
import authService from './api-authorization/AuthorizeService';
import JobPricelist from './NewJobPricelist';
import { ItemOptionList, NewJobQuote, NewJobQuoteItem, JobQuoteExtraItemOption, NewJobQuoteInvoiceExtra, FenceAreaOption } from './models/JobInterfaces';
import { GenericList } from './models/JobSharedInterfaces';

const win: any = window;
const Stimulsoft = win.Stimulsoft;

interface NewJob {
    copyJobId: number;
    systemCompanyId: number;
    chargeFuelSurchargeSwitchedOn: boolean;
    jobDetails: JobDetails;
    jobQuote: NewJobQuote;
    jobFiles: File[];
    jobAddress: JobAddress;
    customers: CustomerList[];
    yesNo: GenericList[];
    jobTypes: GenericList[];
    itemOptions: ItemOptionList[];
    extraItemOptions: JobQuoteExtraItemOption[];
    frequencyOptions: GenericList[];
    statusOptions: GenericList[];
    truckOptions: GenericList[];
    truckSideOptions: GenericList[];
    superSafeSupplierOptions: GenericList[];
    powerPoleLocationOptions: GenericList[];
    powerPoleDistanceOptions: GenericList[];
    epaExtra30MinOnSiteFeeFormatted: string;
    weekendFeeFormatted: string;
    emergencyFeeFormatted: string;
    truckHireRate: number;
    allowAllocateToPastDate: boolean;
    lunchRoomExtraId: number;
    siteOfficeExtraId: number;
    branches: Branch[];
    currentStep: number;
    goingNext: boolean; //used for matching jobs
    saveDisabled: boolean;
    loading: boolean;
}

interface CustomerList {
    id: number;
    name: string;
    gateCode: string;
    accountId: string;
    accountIdDunny: string;
    accountTypeId: number;
    accountType: string;
    accountLimitFormatted: string;
    allowOnGoingHire: boolean;
    quoteNotesDunny: string;
    canEmailCustomer: boolean;
    cashSaleAccount: boolean;
    invoiceQuotedAmount: boolean;
    chargeFuelSurcharge: boolean;
    newFuelInvoice: boolean;
    statusTypeId: number;
    locked: boolean;
}

interface JobDetails {
    id: number;
    copyJobId: number;
    companyId: number;
    customerId: number;
    customer: string;
    customerExists: boolean;
    customerContactId: number;
    customerContactFirstName: string;
    customerContactLastName: string;
    customerContactPhone: string;
    customerContactEmail: string;
    customerAddress: string;
    customerStreetNumber: string;
    customerStreetName: string;
    customerSuburb: string;
    customerState: string;
    customerCountry: string;
    customerPostCode: string;
    customerGooglePlaceId: string;
    customerAddressLatitude: number;
    customerAddressLongitude: number;
    customerAbn: string;
    customerContactEmailConfirmed: boolean;
    customerCashSaleAccount: boolean;
    customerInvoiceQuotedAmount: boolean;   //for any customers where we invoice the quoted amount we don't need to add the note not to go over quoted qty
    customerChargeFuelSurcharge: boolean;   //some customers we don't charge the fuel surcharge for
    customerNewFuelInvoice: boolean;        //some customers have a separate fuel surcharge invoice
    useTyabbPricelist: boolean;
    sendEmailToCustomer: boolean;
    accountTypeId: number;
    accountType: string;
    accountLimitFormatted: string;
    customerAllowOnGoingHire: boolean;
    customerQuoteNotesDunny: string;
    unitNumber: string;
    siteAddressExists: boolean;
    siteAddress: string;
    siteAddressDistance: number;
    siteAddressDistanceFormatted: string;
    siteAddressDuration: number;
    siteAddressDurationFormatted: string;
    addressNearSiteAddress: string;
    streetNumber: string;
    streetName: string;
    suburb: string;
    state: string;
    country: string;
    postCode: string;
    manualStreetName: string;
    manualSuburb: string;
    manualState: string;
    manualCountry: string;
    manualPostCode: string;
    lotNumber: string;
    googlePlaceId: string;
    siteAddressLatitude: number;
    siteAddressLongitude: number;
    addressGooglePlaceId: string;
    addressSiteAddressLatitude: number;
    addressSiteAddressLongitude: number;
    addressSiteAddressDistance: number;
    addressSiteAddressDistanceFormatted: string;
    addressSiteAddressDuration: number;
    addressSiteAddressDurationFormatted: string;
    closestBranchId: number;
    isEvent: boolean;
    isThreeDayHire: boolean;
    eventName: string;
    eventStartDate: string;
    eventEndDate: string;
    deliveryWindowStart: string;
    deliveryWindowEnd: string;
    deliveryPlacementNotes: string;
    jobType: number;
    paymentType: number;
    gateCode: string;
    truckAccess: number;
    parking: number;
    siteClean: number;
    powerlinesWithinRange: number;
    weekdayAccess: number;
    weekendAccess: number;
    phoneCallRequired: number;
    statusId: number;
    notes: string;
    copyQuote: boolean;
    linkJob: boolean;
    linkedToReckon: boolean;
    locked: boolean;
    reference: string;
    priceOnly: boolean; //allows user just to get a price for the cusotmer not create a job
    goAheadOverride: boolean;   //allows the job to goahead even if the customer id locked
}

interface SiteContacts {
    siteContactName: string;
    siteContactPhone: string;
    siteContact2: string;
}

interface JobAddress {
    manualStreetName: string;
    manualSuburb: string;
    manualState: string;
    manualPostCode: string;
    siteAddressExists: boolean;
    siteAddress: string;
}

interface Branch {
    id: number;
    name: string;
    fullAddress: string;
    latitude: number;
    longitude: number;
    scheduleBranchId: number;
}

interface INameToValueMap {
    [key: string]: any;
}

export class NewJobData extends React.Component<RouteComponentProps<{}>, NewJob> {
    constructor(props: any) {
        super(props);

        this.state = {
            copyJobId: 0,
            systemCompanyId: 0,
            chargeFuelSurchargeSwitchedOn: true,
            jobDetails: {
                id: 0,
                companyId: 0,
                copyJobId: 0,
                customer: "",
                customerId: -1,
                customerExists: true,
                customerContactFirstName: "",
                customerContactLastName: "",
                customerContactPhone: "",
                customerContactEmail: "",
                customerAddress: "",
                customerStreetNumber: "",
                customerStreetName: "",
                customerSuburb: "",
                customerState: "",
                customerCountry: "",
                customerPostCode: "",
                customerGooglePlaceId: "",
                customerAddressLatitude: 0,
                customerAddressLongitude: 0,
                customerAbn: "",
                customerContactEmailConfirmed: false,
                customerContactId: -1,
                customerCashSaleAccount: false,
                customerInvoiceQuotedAmount: false,
                customerChargeFuelSurcharge: true,
                customerNewFuelInvoice: false,
                useTyabbPricelist: false,
                sendEmailToCustomer: true,
                accountTypeId: -1,
                accountType: "",
                accountLimitFormatted: "",
                customerAllowOnGoingHire: true,
                customerQuoteNotesDunny: "",
                siteAddressExists: true,
                siteAddress: "",
                siteAddressDistance: 0,
                siteAddressDistanceFormatted: "",
                siteAddressDuration: 0,
                siteAddressDurationFormatted: "",
                addressNearSiteAddress: "",
                unitNumber: "",
                streetNumber: "",
                streetName: "",
                suburb: "",
                state: "",
                country: "",
                postCode: "",
                manualStreetName: "",
                manualSuburb: "",
                manualState: "VIC",
                manualCountry: "Australia",
                manualPostCode: "",
                lotNumber: "",
                isEvent: false,
                isThreeDayHire: false,
                eventName: "",
                eventStartDate: "",
                eventEndDate: "",
                deliveryWindowStart: "",
                deliveryWindowEnd: "",
                deliveryPlacementNotes: "",
                gateCode: "",
                jobType: 1,
                paymentType: -1,
                truckAccess: -1,
                parking: -1,
                siteClean: -1,
                powerlinesWithinRange: -1,
                weekdayAccess: -1,
                weekendAccess: -1,
                phoneCallRequired: -1,
                notes: "",
                copyQuote: true,
                linkJob: false,
                googlePlaceId: "",
                siteAddressLatitude: 0,
                siteAddressLongitude: 0,
                addressGooglePlaceId: "",
                addressSiteAddressLatitude: 0,
                addressSiteAddressLongitude: 0,
                addressSiteAddressDistance: 0,
                addressSiteAddressDistanceFormatted: "",
                addressSiteAddressDuration: 0,
                addressSiteAddressDurationFormatted: "",
                closestBranchId: 1,
                statusId: 1,
                linkedToReckon: true,
                locked: false,
                reference: "",
                priceOnly: false,
                goAheadOverride: false
            },
            jobQuote: {
                siteContactName: "",
                siteContactPhone: "",
                siteContact2: "",
                customerPickingUp: -1,
                jobInstalled: false,
                jobInvoiced: false,
                jobInvoicedDate: "",
                schedulePickup: false,
                paymentType: -1,
                items: [],
                invoiceItems: [],
                tiltSlideClearance: -1,
                cameraLocation: "",
                powerPoleQuestions: {
                    isUnderground: false,
                    superSafeConnecting: -1,
                    superSafeSupplierId: -1,
                    powerPoleLocationId: -1,
                    powerPreviouslyConnected: -1,
                    powerAbolished: -1,
                    siteLocked: -1,
                    clearSightLine: -1,
                    pitOnSite: -1,
                    pitDistanceId: -1,
                    superSafeDiggingTrench: -1
                },
                fenceQuestions: {
                    fenceTotalMetres: 0,
                    treeProtection: false,
                    highWindArea: false,
                    shadeClothAdded: false,
                    signageAdded: false,
                    signagePanels: 0,
                    fenceAreas: []
                },
                epaQuestions: {
                    tankLidExposed: -1,
                    tankLidLocationKnown: -1,
                    tankLidLocation: "",
                    truckLargeAccess: -1,
                    truckSmallAccess: -1,
                    truckAccessToTank: -1,  //within 25m
                    steepDriveway: -1,
                    hoseMetres: 0,
                    sealedDriveway: -1,
                    lastService: "",
                    waterBoard: "",
                    waterAccessOnSite: -1,
                    emergencySameDayJob: -1,
                    weekendJob: -1,
                    siteVisit: -1,
                    confinedSpace: -1,
                    materialSafetyDataSheet: -1,
                    sampleTestingRequired: -1
                },
                total: 0,
                totalFormatted: "$0.00",
                invoiceTotal: 0,
            },
            jobFiles: [],
            jobAddress: {
                manualStreetName: "",
                manualSuburb: "",
                manualState: "",
                manualPostCode: "",
                siteAddressExists: false,
                siteAddress: ""
            },
            customers: [],
            yesNo: [],
            jobTypes: [],
            itemOptions: [],
            extraItemOptions: [],
            frequencyOptions: [],
            statusOptions: [],
            truckOptions: [],
            truckSideOptions: [],
            powerPoleDistanceOptions: [],
            powerPoleLocationOptions: [],
            superSafeSupplierOptions: [],
            epaExtra30MinOnSiteFeeFormatted: "",
            weekendFeeFormatted: "",
            emergencyFeeFormatted: "",
            truckHireRate: 0,
            branches: [],
            allowAllocateToPastDate: false,
            lunchRoomExtraId: 0,
            siteOfficeExtraId: 0,
            currentStep: 1,
            goingNext: false,
            saveDisabled: false,
            loading: true
        };
    }

    componentDidMount() {
        //document.title = "New Job";
        let jobParams: INameToValueMap = {};
        jobParams["typeId"] = 1;
        jobParams["copyId"] = 0;
        jobParams = this.props.match.params;
        let typeId = jobParams["typeId"];
        let copyId = jobParams["copyId"] ?? 0;
        this.getData(typeId, copyId);
    }

    componentDidUpdate(prevProps: any) {
        if (this.props.match.params !== prevProps.match.params) {
            let jobParams: INameToValueMap = {};
            jobParams["typeId"] = 1;
            jobParams["copyId"] = 0;
            jobParams = this.props.match.params;
            let typeId = jobParams["typeId"];
            let copyId = jobParams["copyId"] ?? 0;
            this.getData(typeId, copyId);
        }
    }

    async getData(typeId: number, copyId: number) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckUserPermission?Permission=CreateJob&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                axios.get('api/Job/GetDropdowns?JobId=0&SubId=' + user.sub, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    var systemCompanyId = res.data.systemCompanyId;
                    var jobDetails = this.resetJobDetails();
                    jobDetails.jobType = typeId * 1; //systemCompanyId === 2 ? 1 : 2; //use quote for scaffold and pricelist for dunnys as default
                    if (res.data.isTyabbUser) {
                        jobDetails.useTyabbPricelist = true;
                    }
                    let fenceAreas = res.data.fenceAreas;
                    var jobQuote = this.resetJobQuote(fenceAreas);
                    //add blank row
                    var quoteItem = this.addBlankItem(0);
                    jobQuote.items.push(quoteItem);

                    var jobAddress = this.resetJobAddress();

                    this.setState({
                        jobDetails: jobDetails, jobQuote: jobQuote, jobAddress: jobAddress, jobFiles: [], branches: res.data.branchOptions,
                        systemCompanyId: systemCompanyId, 
                        allowAllocateToPastDate: res.data.allowAllocateToPastDate, truckOptions: res.data.truckOptions, 
                        customers: res.data.customers, yesNo: res.data.yesNo, jobTypes: res.data.jobTypes, itemOptions: res.data.itemOptions,
                        extraItemOptions: res.data.extraItemOptions, frequencyOptions: res.data.frequencyOptions, statusOptions: res.data.statusOptions, 
                        truckSideOptions: res.data.truckSideOptions, powerPoleDistanceOptions: res.data.powerPoleDistanceOptions,
                        powerPoleLocationOptions: res.data.powerPoleLocationOptions, superSafeSupplierOptions: res.data.superSafeSupplierOptions,
                        lunchRoomExtraId: res.data.lunchRoomExtraId, siteOfficeExtraId: res.data.siteOfficeExtraId,
                        epaExtra30MinOnSiteFeeFormatted: res.data.epaExtra30MinOnSiteFeeFormatted, truckHireRate: res.data.truckHireRate, chargeFuelSurchargeSwitchedOn: res.data.chargeFuelSurchargeSwitchedOn,
                        emergencyFeeFormatted: res.data.emergencyFeeFormatted, weekendFeeFormatted: res.data.weekendFeeFormatted, currentStep: 1, goingNext: false
                    });
                    
                    if (copyId > 0) {
                        //load the job details based on the job that is being copied from
                        axios.get('api/Job/GetCopyJobDetails?jobId=' + copyId + '&subid=' + user.sub, {
                            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                        })
                        .then(res => {
                            var jobDetails: JobDetails = res.data.jobDetails;
                            jobDetails.id = 0;  //set the id back to 0 as it is a new job
                            jobDetails.statusId = 1; //new job status needs to be initial enquiry
                            jobDetails.copyQuote = true;
                            jobDetails.linkJob = systemCompanyId === 2 ? true : false;
                            jobDetails.copyJobId = copyId;
                            jobDetails.accountTypeId = res.data.accountTypeId;
                            jobDetails.customerAllowOnGoingHire = res.data.allowOnGoingHire;
                            jobDetails.customerQuoteNotesDunny = res.data.quoteNotesDunny;
                            jobDetails.customerExists = true;
                            jobDetails.useTyabbPricelist = false;

                            var jobQuote = res.data.jobQuote;
                            //add blank row
                            var quoteItems = jobQuote.items;
                            var quoteItem = this.addBlankItem(quoteItems.length);

                            quoteItems.push(quoteItem);

                            this.setState({ copyJobId: copyId, jobDetails: jobDetails, jobQuote: res.data.jobQuote, loading: false });
                        })
                        .catch(error => {
                            toast.error(error.message);
                        });
                    } else {
                        this.setState({ loading: false });
                    }
                })
                .catch(error => {
                    toast.error(error.message);
                });
            } else {
                //redirect to 403 permission denied
                this.props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    resetJobDetails = () => {
        var jobDetails: JobDetails = {
            id: 0,
            companyId: 0,
            copyJobId: 0,
            customer: "",
            customerExists: true,
            customerId: -1,
            customerContactFirstName: "",
            customerContactLastName: "",
            customerContactPhone: "",
            customerContactEmail: "",
            customerAddress: "",
            customerStreetNumber: "",
            customerStreetName: "",
            customerSuburb: "",
            customerState: "",
            customerCountry: "",
            customerPostCode: "",
            customerGooglePlaceId: "",
            customerAddressLatitude: 0,
            customerAddressLongitude: 0,
            customerAbn: "",
            customerContactEmailConfirmed: false,
            customerContactId: -1,
            customerCashSaleAccount: false,
            customerInvoiceQuotedAmount: false,
            customerChargeFuelSurcharge: true,
            customerNewFuelInvoice: false,
            useTyabbPricelist: false,
            sendEmailToCustomer: true,
            accountTypeId: -1,
            accountType: "",
            accountLimitFormatted: "",
            customerAllowOnGoingHire: true,
            customerQuoteNotesDunny: "",
            siteAddressExists: true,
            siteAddress: "",
            siteAddressDistance: 0,
            siteAddressDistanceFormatted: "",
            siteAddressDuration: 0,
            siteAddressDurationFormatted: "",
            addressNearSiteAddress: "",
            unitNumber: "",
            streetNumber: "",
            streetName: "",
            suburb: "",
            state: "",
            country: "",
            postCode: "",
            manualStreetName: "",
            manualSuburb: "",
            manualState: "VIC",
            manualCountry: "Australia",
            manualPostCode: "",
            lotNumber: "",
            isEvent: false,
            isThreeDayHire: false,
            eventName: "",
            eventStartDate: "",
            eventEndDate: "",
            deliveryWindowStart: "",
            deliveryWindowEnd: "",
            deliveryPlacementNotes: "",
            gateCode: "",
            jobType: 1,
            paymentType: -1,
            truckAccess: -1,
            parking: -1,
            siteClean: -1,
            powerlinesWithinRange: -1,
            weekdayAccess: -1,
            weekendAccess: -1,
            phoneCallRequired: -1,
            notes: "",
            copyQuote: true,
            linkJob: false,
            googlePlaceId: "",
            siteAddressLatitude: 0,
            siteAddressLongitude: 0,
            addressGooglePlaceId: "",
            addressSiteAddressLatitude: 0,
            addressSiteAddressLongitude: 0,
            addressSiteAddressDistance: 0,
            addressSiteAddressDistanceFormatted: "",
            addressSiteAddressDuration: 0,
            addressSiteAddressDurationFormatted: "",
            closestBranchId: 1,
            statusId: 1,
            linkedToReckon: true,
            locked: false,
            reference: "",
            priceOnly: false,
            goAheadOverride: false
        };
 
        return jobDetails;
    }

    resetJobQuote = (fenceAreas: FenceAreaOption[]) => {
        var jobQuote: NewJobQuote = {
            siteContactName: "",
            siteContactPhone: "",
            siteContact2: "",
            customerPickingUp: -1,
            jobInstalled: false,
            jobInvoiced: false,
            jobInvoicedDate: "",
            schedulePickup: false,
            paymentType: -1,
            items: [],
            invoiceItems: [],
            tiltSlideClearance: -1,
            cameraLocation: "",
            powerPoleQuestions: {
                isUnderground: false,
                superSafeConnecting: -1,
                superSafeSupplierId: -1,
                powerPoleLocationId: -1,
                powerPreviouslyConnected: -1,
                powerAbolished: -1,
                siteLocked: -1,
                clearSightLine: -1,
                pitOnSite: -1,
                pitDistanceId: -1,
                superSafeDiggingTrench: -1
            },
            fenceQuestions: {
                fenceTotalMetres: 0,
                treeProtection: false,
                highWindArea: false,
                shadeClothAdded: false,
                signageAdded: false,
                signagePanels: 0,
                fenceAreas: fenceAreas
            },
            epaQuestions: {
                tankLidExposed: -1,
                tankLidLocationKnown: -1,
                tankLidLocation: "",
                truckLargeAccess: -1,
                truckSmallAccess: -1,
                truckAccessToTank: -1,  //within 25m
                steepDriveway: -1,
                hoseMetres: 0,
                sealedDriveway: -1,
                lastService: "",
                waterBoard: "",
                waterAccessOnSite: -1,
                emergencySameDayJob: -1,
                weekendJob: -1,
                siteVisit: -1,
                confinedSpace: -1,
                materialSafetyDataSheet: -1,
                sampleTestingRequired: -1
            },
            total: 0,
            totalFormatted: "$0.00",
            invoiceTotal: 0
        };

        return jobQuote;
    }

    resetJobAddress = () => {
        var jobAddress = {
            manualStreetName: "",
            manualSuburb: "",
            manualState: "",
            manualPostCode: "",
            siteAddressExists: false,
            siteAddress: ""
        };

        return jobAddress;
    }

    addBlankItem = (itemIndex: number) => {
        var quoteItem: NewJobQuoteItem = {
            itemIndex: itemIndex,
            itemId: -1,
            item: "",
            itemTypeId: 0,
            productGroupId: 0,
            itemDescription: "",
            installOnly: false,
            extendable: true,
            isEws: false,
            isWaste: false,
            isUnderground: false,
            isEvent: false,
            unitNumber: "",
            scheduleDate: "",
            onHireDate: "",
            offhireDate: "",
            pickupDate: "",
            hirePeriod: 0,
            defaultHirePeriod: 0,
            customHirePeriod: false,
            frequencyId: -1,
            locationId: -1,
            fenceBraceId: -1,
            hours: 0,
            size: 0,
            quantity: 1,
            unitOfMeasure: "",
            initialHire: 0,
            travel: 0,
            cleaning: 0,
            trench: 0,
            confinedSpace: 0,
            fuelSurcharge: 0,
            amount: 0,
            overhireRate: "0.00",
            truck: 0,
            truckDoors: -1,
            purchaseOrderNumber: "",
            purchaseOrderNumberFuel: "",
            notes: "",
            trolley: false,
            extras: {
                itemId: -1,
                quantity: 0,
                equipped: false
            },
            fencingStock: {
                panels: 0,
                blocks: 0,
                clamps: 0,
                pickets: 0,
                signs: 0,
                braces: 0
            },
            calculatedPriceNote: "",
            deliveryAddress: "",
            deliveryAddressGooglePlaceId: "",
            deliveryAddressLatitude: 0,
            deliveryAddressLongitude: 0,
            invoiceDescription: "",
            incStepsTravel: false,
            invoicePeriodTypeId: 1
        };
        return quoteItem;
    }

    reloadJobDetails = () => {
        //not used for new job only existing
    }

    openNewJob = (newJobId: number) => {
        //not used for new job only existing - for job transfer
    }

    updateJobDetails = (jobDetails: JobDetails, resetItems: boolean) => {
        //save the data to state - so we can pass to other items
        var jobAddress: JobAddress = {
            manualStreetName: jobDetails.manualStreetName,
            manualSuburb: jobDetails.manualSuburb,
            manualState: jobDetails.manualState,
            manualPostCode: jobDetails.manualPostCode,
            siteAddressExists: jobDetails.siteAddressExists,
            siteAddress: jobDetails.siteAddress
        };

        //check if we need to reset the items
        var jobQuote = this.state.jobQuote;
        if (resetItems) {
            var quoteItems: NewJobQuoteItem[] = [];
            var item = this.addBlankItem(0);
            quoteItems.push(item);
            jobQuote.items = quoteItems;
        }

        this.setState({ jobDetails: jobDetails, jobAddress: jobAddress, jobQuote: jobQuote });
    }

    updatePaymentType = (accountTypeId: number) => {
        //set payment type for account customers ONLY
        var quote = this.state.jobQuote;
       
        if (accountTypeId === 2) {
            quote.paymentType = 1;
        } else if (accountTypeId === 3) {
            quote.paymentType = 2;
        } else {
            quote.paymentType = -1;
        }

        this.setState({ jobQuote: quote });
    }

    updateJobQuote = (jobQuote: NewJobQuote) => {
        //update total
        var total = 0;
        var invoiceTotal = 0;

        for (var i = 0; i < jobQuote.items.length; i++) {
            var amount = jobQuote.items[i].amount * 1;
            total += amount;
        }

        //include extra invoice items
        invoiceTotal += total;
        for (var i = 0; i < jobQuote.invoiceItems.length; i++) {
            invoiceTotal += jobQuote.invoiceItems[i].amount * 1;
        }

        jobQuote.total = total;
        jobQuote.totalFormatted = (total).toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        jobQuote.invoiceTotal = invoiceTotal;

        this.setState({ jobQuote: jobQuote });
    }

    updateSiteContacts = (siteContacts: SiteContacts) => {
        var quote = this.state.jobQuote;
        quote.siteContactName = siteContacts.siteContactName; 
        quote.siteContactPhone = siteContacts.siteContactPhone;
        quote.siteContact2 = siteContacts.siteContact2;
        this.setState({ jobQuote: quote });
    }

    //type 1: add files, type 2: replace files
    updateJobFiles = (jobFiles: File[], type: number) => {
        var files = this.state.jobFiles;

        if (type === 1) {
            for (var i = 0; i < jobFiles.length; i++) {
                files.push(jobFiles[i]);
            }
        } else {
            files = jobFiles;
        }

        this.setState({ jobFiles: files });
    }

    addInvoiceItem = (invoiceItem: NewJobQuoteInvoiceExtra) => {
        let quote = this.state.jobQuote;
        quote.invoiceItems.push(invoiceItem);

        //calculate price
        var extraInvItemsPrice = this.calculateExtraItemsTotal(quote.invoiceItems);
        let totalInvPrice = (quote.total * 1) + extraInvItemsPrice;
        quote.invoiceTotal = totalInvPrice;

        this.setState({ jobQuote: quote });
    }

    deleteInvoiceItem = (deleteIndex: number) => {
        let quote = this.state.jobQuote;
        let quoteInvoiceItems = quote.invoiceItems;
        quoteInvoiceItems.splice(deleteIndex, 1);

        //calculate price
        var extraInvItemsPrice = this.calculateExtraItemsTotal(quoteInvoiceItems);
        let totalInvPrice = (quote.total * 1) + extraInvItemsPrice;
        quote.invoiceTotal = totalInvPrice;

        this.setState({ jobQuote: quote });
    }

    calculateExtraItemsTotal = (extraInvoiceItems: NewJobQuoteInvoiceExtra[]) => {
        var extraInvItemTotal = 0;
        for (var i = 0; i < extraInvoiceItems.length; i++) {
            extraInvItemTotal += extraInvoiceItems[i].amount * 1;
        }

        return extraInvItemTotal;
    }

    updateJobNotes = (notes: string) => {
        let jobDetails = this.state.jobDetails;
        jobDetails.notes = notes;

        this.setState({ jobDetails: jobDetails });
    }

    updateGateCode = (updatedGateCode: string) => {
        let jobDetails = this.state.jobDetails;
        jobDetails.gateCode = updatedGateCode;

        this.setState({ jobDetails: jobDetails });
    }

    _next = () => {
        //if we are on the last step and user clicks finish button, then save to the database
        if (this.state.currentStep == 5) {
            if (this.state.saveDisabled) {
                return;
            }
            this.setState({ saveDisabled: true });

            if (this.state.jobDetails.priceOnly) {
                toast.error("You cannot save PRICE ONLY jobs. Remove the Price Only check from Job Details screen.");
            } else {
                this.saveJobDetails();
            }
        } else {
            let currentStep = this.state.currentStep;
            // Make sure currentStep is set to something reasonable
            if (currentStep >= 4) {
                currentStep = 5;
            } else {
                var jobTypeId = (this.state.jobDetails.jobType * 1);

                if (currentStep === 2 && jobTypeId === 1) {
                    currentStep = currentStep + 2;  //skip pricelist and confirmation step as job will be quoted later
                }
                currentStep = currentStep + 1;
            }

            this.setState({
                currentStep: currentStep, goingNext: true
            });
        }
    }

    getJobQuoteItems = (quoteItems: NewJobQuoteItem[]) => {
        //quoteItems.pop()
        var quoteItemsToSave: NewJobQuoteItem[] = [];
        for (var i = 0; i < quoteItems.length - 1; i++) {
            var quoteItemToSave = quoteItems[i];
            if (quoteItemToSave.customHirePeriod && quoteItemToSave.invoicePeriodTypeId === 1) {
                //need to change hire period to days for saving (don't convert monthly hire periods)
                quoteItemToSave.hirePeriod = quoteItems[i].hirePeriod * 7;
            }
            quoteItemsToSave.push(quoteItemToSave);
        }

        return quoteItemsToSave;
    }

    async saveJobDetails() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        let jobQuote = this.state.jobQuote;
        //let items = jobQuote.items.pop();
        let items = this.getJobQuoteItems(jobQuote.items);
        let files = this.state.jobFiles;

        var jobType = this.state.jobDetails.jobType * 1;
        if (jobType === 1) {
            //clear job quote as pricelist hasn't been selected
            jobQuote = {
                siteContactName: "",
                siteContactPhone: "",
                siteContact2: "",
                customerPickingUp: -1,
                jobInstalled: false,
                jobInvoiced: false,
                jobInvoicedDate: "",
                schedulePickup: false,
                paymentType: -1,
                items: [],
                invoiceItems: [],
                tiltSlideClearance: -1,
                cameraLocation: "",
                powerPoleQuestions: {
                    isUnderground: false,
                    superSafeConnecting: -1,
                    superSafeSupplierId: -1,
                    powerPoleLocationId: -1,
                    powerPreviouslyConnected: -1,
                    powerAbolished: -1,
                    siteLocked: -1,
                    clearSightLine: -1,
                    pitOnSite: -1,
                    pitDistanceId: -1,
                    superSafeDiggingTrench: -1
                },
                fenceQuestions: {
                    fenceTotalMetres: 0,
                    treeProtection: false,
                    highWindArea: false,
                    shadeClothAdded: false,
                    signageAdded: false,
                    signagePanels: 0,
                    fenceAreas: []
                },
                epaQuestions: {
                    tankLidExposed: -1,
                    tankLidLocationKnown: -1,
                    tankLidLocation: "",
                    truckLargeAccess: -1,
                    truckSmallAccess: -1,
                    truckAccessToTank: -1,  //within 25m
                    steepDriveway: -1,
                    hoseMetres: 0,
                    sealedDriveway: -1,
                    lastService: "",
                    waterBoard: "",
                    waterAccessOnSite: -1,
                    emergencySameDayJob: -1,
                    weekendJob: -1,
                    siteVisit: -1,
                    confinedSpace: -1,
                    materialSafetyDataSheet: -1,
                    sampleTestingRequired: -1
                },
                total: 0,
                totalFormatted: "$0.00",
                invoiceTotal: 0
            };
        } else {
            jobQuote.items = items;
        }

        var jobDetails = {
            jobDetails: this.state.jobDetails,
            jobQuote: jobQuote,
            subId: user.sub
        };

        axios.post('api/Job/SaveNewJobDetails', jobDetails, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Job Saved");
                var jobId = res.data.id;

                //email job request to customer
                var reportName = jobType === 2 ? "JobRequest" : jobType === 3 ? "JobQuoteRequest" : "";
                if (this.state.systemCompanyId === 3 && reportName != "") {
                    //this will either send the email or just save the file/s to the job
                    this.sendReport(reportName, jobId, files, user.sub);
                } else {
                    //upload any files added to the job or go straight to the new job
                    if (files.length > 0) {
                        this.saveJobFiles(res.data.id, files, user.sub, token ?? "");
                    } else {
                        //then need to go to job search?
                        this.props.history.push("/job/" + res.data.id)
                    }
                }
            }
        })
        .catch(error => {
            toast.error(error.message);
            this.setState({ saveDisabled: false });
        });
    }


    sendReport = async (reportName: string, jobId: number, files: File[], subId: string) => {
        const token = await authService.getAccessToken();

        //get the data
        //set param1 to 0 as that is for quote version id which isn't needed here as there is only one version of the quote
        axios.get('api/Report/Get' + reportName + 'Data?jobId=' + jobId + "&reportName=" + reportName + "&param1=0", {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                //Run Job Request / Quote Request report
                var report = new Stimulsoft.Report.StiReport();
                var reportFile = Stimulsoft.System.IO.File.getFile("reports/" + res.data.reportFileName);
                report.load(reportFile);
                report.dictionary.dataStore.clear();

                var reportData = JSON.stringify(res.data.reportData);
                //Dataset created
                var dataSet = new Stimulsoft.System.Data.DataSet("data");
                dataSet.readJson(reportData)
                report.regData(dataSet.dataSetName, "", dataSet);
                report.reportName = res.data.reportName;
                report.dictionary.connect(false);
                report.dictionary.synchronize();

                //add relationships between data
                for (var i = 0; i < res.data.reportRelationships.length; i++) {
                    var dataRelation = new Stimulsoft.Report.Dictionary.StiDataRelation(res.data.reportRelationships[i].sourceName, res.data.reportRelationships[i].name, res.data.reportRelationships[i].alias, report.dictionary.dataSources.getByName(res.data.reportRelationships[i].parentTableName), report.dictionary.dataSources.getByName(res.data.reportRelationships[i].childTableName), [res.data.reportRelationships[i].parentFieldName], [res.data.reportRelationships[i].childFieldName]);
                    report.dictionary.relations.add(dataRelation);
                    report.dictionary.synchronize();
                }

                //Download the report to a pdf file
                var fileName = reportName.toLowerCase().includes("quote") ? "Quote Request Confirmation" : "Order Confirmation";

                report.renderAsync(() => {

                    // Export Asynchronously
                    report.exportDocumentAsync(async (pdfData: Uint8Array) => {
                        const bytes = new Uint8Array(pdfData);

                        // Code inside this callback runs ONLY when the PDF is 100% finished
                        //Stimulsoft.System.StiObject.saveAs(pdfData, fileName, "application/pdf");  - use this code to download to browser
                        const saveFile = new File([bytes], fileName, { type: "application/pdf" });

                        //Send the report to the back-end to send the email to the customer
                        const data = new FormData()
                        data.append('jobId', jobId.toString());
                        data.append('sendEmail', this.state.jobDetails.sendEmailToCustomer.toString());
                        data.append('subId', subId);
                        data.append('report', saveFile);
                        //data.append('report', saveFile, fileName + '.pdf');
                        for (var i = 0; i < files.length; i++) {
                            data.append('plan', files[i]);      //uploading plans at the same time so I don't need to do 2 separate calls
                        }

                        //email confirmation, T&C's to customer if email box ticked otherwise just save files 
                        //upload any files added to the job
                        axios.post('api/Job/SendConfirmationEmail', data, {
                            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                        })
                        .then(res => {
                            if (res.data.isError) {
                                var errors = res.data.messages as any[];
                                errors.map(function (error: any) {
                                    toast.error(error.content);
                                });
                                this.props.history.push("/job/" + jobId);   //going to the job anyway as job is saved successfully
                            } else {
                                this.props.history.push("/job/" + jobId);
                            }
                        })
                        .catch(error => {
                            toast.error(error.message);
                        });
                    }, Stimulsoft.Report.StiExportFormat.Pdf);
                });
            }
        });
            
    }

    saveJobFiles = (jobId: number, files: File[], subId: string, token: string) => {
        //save job files
        const data = new FormData()
        data.append('jobId', jobId.toString());
        data.append('subId', subId);
        for (var i = 0; i < files.length; i++) {
            data.append('plan', files[i]);
        }

        //save job files
        axios.post('api/Job/SaveJobQuotePlans', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
                this.setState({ saveDisabled: false });
            } else {
                toast.success("Plan/s have been saved");
                //then need to go to job search?
                this.props.history.push("/job/" + jobId)
            }
        })
        .catch(error => {
            this.setState({ saveDisabled: false });
            toast.error(error.message);
        });
    }

    _prev = () => {
        let currentStep = this.state.currentStep;
        if (currentStep <= 1) {
            currentStep = 1;
        } else {
            var jobTypeId = (this.state.jobDetails.jobType * 1);

            if (currentStep === 5 && jobTypeId === 1) {
                currentStep = currentStep - 2;  //skip pricelist and confirmation step as job will be quoted later
            }

            currentStep = currentStep - 1;
        }

        this.setState({
            currentStep: currentStep, goingNext: false
        });
    }

    public render() {
        let currentStep = this.state.currentStep;
        let jobDetails = this.state.jobDetails;

        var contents;
        if (currentStep == 1) {
            let siteContacts: SiteContacts = {
                siteContactName: this.state.jobQuote.siteContactName,
                siteContactPhone: this.state.jobQuote.siteContactPhone,
                siteContact2: this.state.jobQuote.siteContact2
            };
            contents = <JobDetail currentStep={currentStep} jobDetails={jobDetails} siteContacts={siteContacts} systemCompanyId={this.state.systemCompanyId} customers={this.state.customers} yesNo={this.state.yesNo} jobTypes={this.state.jobTypes} statusOptions={this.state.statusOptions} branches={this.state.branches} canReopenJob={false} canCancelJob={false} canUnlockJob={false} copyJob={this.state.copyJobId > 0} save={this._next} reloadJobDetails={this.reloadJobDetails} update={this.updateJobDetails} updateSiteContacts={this.updateSiteContacts} updatePaymentType={this.updatePaymentType} openNewJob={this.openNewJob} />
        }
        else if (currentStep == 2) {
            contents = <JobMatchingJobs currentStep={currentStep} jobAddress={this.state.jobAddress} goingNext={this.state.goingNext} goPrev={this._prev} goNext={this._next} />
        }
        else if (currentStep == 3) {
            let siteAddressDuration = jobDetails.siteAddressDuration === 0 ? jobDetails.addressSiteAddressDuration : jobDetails.siteAddressDuration;
            contents = <JobPricelist currentStep={currentStep} customerId={jobDetails.customerId} priceOnly={jobDetails.priceOnly} jobTypeId={jobDetails.jobType} isEvent={jobDetails.isEvent} isThreeDayHire={jobDetails.isThreeDayHire} useTyabbPricelist={jobDetails.useTyabbPricelist} accountTypeId={jobDetails.accountTypeId} customerQuoteNotesDunny={jobDetails.customerQuoteNotesDunny} customerInvoiceQuotedAmount={jobDetails.customerInvoiceQuotedAmount} siteAddressDuration={siteAddressDuration} itemOptions={this.state.itemOptions} extraItemOptions={this.state.extraItemOptions} frequencyOptions={this.state.frequencyOptions} gateCode={this.state.jobDetails.gateCode} powerPoleDistanceOptions={this.state.powerPoleDistanceOptions} powerPoleLocationOptions={this.state.powerPoleLocationOptions} superSafeSupplierOptions={this.state.superSafeSupplierOptions} epaExtra30MinOnSiteFeeFormatted={this.state.epaExtra30MinOnSiteFeeFormatted} emergencyFeeFormatted={this.state.emergencyFeeFormatted} weekendFeeFormatted={this.state.weekendFeeFormatted} truckHireRate={this.state.truckHireRate} allowAllocateToPastDate={this.state.allowAllocateToPastDate} lunchRoomExtraId={this.state.lunchRoomExtraId} siteOfficeExtraId={this.state.siteOfficeExtraId} truckOptions={this.state.truckOptions} truckSideOptions={this.state.truckSideOptions} yesNoOptions={this.state.yesNo} notes={this.state.jobDetails.notes} quote={this.state.jobQuote} files={this.state.jobFiles} customerChargeFuelSurcharge={jobDetails.customerChargeFuelSurcharge} customerNewFuelInvoice={jobDetails.customerNewFuelInvoice} chargeFuelSurchargeSwitchedOn={this.state.chargeFuelSurchargeSwitchedOn} goPrev={this._prev} updateNotes={this.updateJobNotes} updateGateCode={this.updateGateCode} update={this.updateJobQuote} updateFiles={this.updateJobFiles} goNext={this._next} />
        }
        else if (currentStep == 4) {
            var jobQuoteInvTotal = this.state.jobQuote.invoiceTotal * 1;
            var jobQuoteTotal = this.state.jobQuote.total * 1;
            let invTotal = jobQuoteInvTotal === 0 ? jobQuoteTotal : jobQuoteInvTotal;
            let contact = this.state.jobDetails.customerContactFirstName + ' ' + this.state.jobDetails.customerContactLastName + ' - ' + this.state.jobDetails.customerContactPhone + ' - ' + this.state.jobDetails.customerContactEmail;
            contents = <NewJobInvoice currentStep={currentStep} siteAddress={this.state.jobDetails.siteAddress} customerName={this.state.jobDetails.customer} contact={contact} extraInvoiceItems={this.state.jobQuote.invoiceItems} quoteItems={this.state.jobQuote.items} invoiceTotal={invTotal} addInvoiceItem={this.addInvoiceItem} deleteInvoiceItem={this.deleteInvoiceItem} goPrev={this._prev} goNext={this._next} />
        }
        else if (currentStep == 5) {
            contents = <NewJobNote currentStep={currentStep} saveDisabled={this.state.saveDisabled} notes={jobDetails.notes} goPrev={this._prev} update={this.updateJobNotes} goNext={this._next}  />
        } 

        let contentsFinal = this.state.loading
            ? <p><em>Loading...</em></p>
            : contents;

        return <div>
            <h1>New {this.state.jobDetails.jobType === 3 ? "Quote" : "Job"}</h1>
            <div className={this.state.systemCompanyId === 3 && (this.state.jobDetails.jobType === 1 || this.state.jobDetails.jobType === 3) ? "newJobPricelist--prepaid" : "hidden"}>QUOTE ONLY - This job WILL NOT be scheduled until customer go-ahead received!</div>
            {contentsFinal}
        </div>;
        
    }
    
}